import { METRO_PARENT_MAP, SearchPageCity } from 'src/constants/searchPages'

type FaqItem = {
  question: string
  answer: string
}

type EditorialSection = {
  title: string
  content: string
}

export type CitySeoOverride = {
  /** Overrides the templated title tag (city-level only, not hoods/satellites) */
  TITLE?: string
  /** Overrides the H1 heading on the page (city-level only) */
  H1?: string
  /** Overrides the templated meta description (city-level only, not hoods/satellites) */
  DESCRIPTION?: string
  /** City-specific FAQs — replace pillar-level generic FAQs in both schema and visible section */
  FAQS?: FaqItem[]
  /** Editorial H2 sections rendered above the FAQ in SeoFooter */
  EDITORIAL?: EditorialSection[]
}

/**
 * Look up city SEO content from i18n, with metro parent fallback.
 *
 * Pass the pillar `t()` function so we can look up `CITY_SEO.{city}`.
 * Satellite cities (e.g. Apodaca, Zapopan) fall back to their metro parent
 * (Monterrey, Guadalajara) for FAQs and editorial.
 *
 * - City-level pages with their own CITY_SEO: get full override
 * - Satellite cities: inherit FAQs + editorial from metro parent (no title/description)
 * - Hood-level pages: inherit FAQs + editorial from city (no title/description)
 */
export function parseCitySeo(
  raw: unknown,
  city: SearchPageCity | null,
  hood: string | null,
  parentRaw?: unknown
): CitySeoOverride | null {
  if (!city) return null

  // Try direct city data first
  const directData = parseRaw(raw)

  // If no direct data, try metro parent (e.g. Apodaca → Monterrey)
  const metroParent = METRO_PARENT_MAP[city]
  const parentData = metroParent ? parseRaw(parentRaw) : null

  // Merge: direct data takes priority, fall back to parent for missing fields
  const data = directData ?? parentData
  if (!data) return null

  const isSatellite = !directData && !!parentData
  const isHood = !!hood

  // Satellite cities and hood pages: inherit FAQs + editorial, NOT title/description
  if (isHood || isSatellite) {
    if (!data.FAQS?.length && !data.EDITORIAL?.length) return null
    return {
      FAQS: data.FAQS,
      EDITORIAL: data.EDITORIAL
    }
  }

  return data
}

function parseRaw(raw: unknown): CitySeoOverride | null {
  if (!raw || typeof raw === 'string') return null
  const data = raw as CitySeoOverride
  if (
    !data.TITLE &&
    !data.H1 &&
    !data.DESCRIPTION &&
    !data.FAQS?.length &&
    !data.EDITORIAL?.length
  ) {
    return null
  }
  return data
}
